import { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    GlobalStyles,
    IconButton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import { apiFetch } from '../../lib/api';
import type { SwmsSignoff, SwmsTask } from '../../hooks/useJobs';

/**
 * A signed SWMS in a popup, printable. Replaces the legacy `jobs/pdf/<job>/<record>`
 * TCPDF document, which wrote a file to the web server on every view and broke
 * when that folder went away. Same content; Print saves a PDF.
 *
 * The document is a white "paper" regardless of the app theme. While this dialog
 * is open, print styles hide everything else on the page (the app, the job
 * dialog underneath) so only the SWMS prints.
 */

interface SwmsRecordData {
    readonly job: {
        readonly id: number;
        readonly jobNumber: number;
        readonly address: string;
        readonly clientName: string | null;
    };
    readonly signoff: SwmsSignoff;
    readonly tasks: readonly SwmsTask[];
}

const EMPLOYER = 'Silverline Construction Group PTY LTD';

function ddmmyyyy(iso: string): string {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('en-AU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'Australia/Melbourne',
    })
        .format(d)
        .replaceAll('/', '-');
}

const cell = { border: '1px solid #000', padding: '4px 8px', verticalAlign: 'top', textAlign: 'left' } as const;
const head = { ...cell, fontWeight: 700, background: '#e8e8e8' } as const;

function Table({ children }: { children: React.ReactNode }): React.JSX.Element {
    return (
        <Box
            component="table"
            sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt', mb: 3, breakInside: 'auto' }}
        >
            <tbody>{children}</tbody>
        </Box>
    );
}

function Row({ cells, header = false }: { cells: readonly React.ReactNode[]; header?: boolean }): React.JSX.Element {
    return (
        <tr style={{ breakInside: 'avoid' }}>
            {cells.map((c, i) => (
                <td key={i} style={header ? head : cell}>
                    {c}
                </td>
            ))}
        </tr>
    );
}

function SwmsDocument({ data }: { data: SwmsRecordData }): React.JSX.Element {
    const { job, signoff, tasks } = data;
    const signedDate = ddmmyyyy(signoff.signedAt);

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                color: '#000',
                p: { xs: 2, sm: 4 },
                fontFamily: 'Arial, sans-serif',
                fontSize: '10pt',
                '@media print': { my: 0, p: 0, maxWidth: 'none' },
            }}
        >
            {/* ─── Cover ─────────────────────────────────────────────── */}
            <Box sx={{ border: '1px solid #000', p: 3, mb: 4, textAlign: 'center' }}>
                <Box
                    component="img"
                    src="/silverline-logo-print.png"
                    alt="Silverline Construction Group"
                    sx={{ width: '100%', maxWidth: 420, height: 'auto', mb: 2 }}
                />
                <Typography sx={{ fontFamily: 'inherit', fontSize: '14pt', fontWeight: 700 }}>
                    Silverline Construction Group - SWMS
                </Typography>
                <Typography sx={{ fontFamily: 'inherit', fontSize: '12pt', mt: 1 }}>
                    Builder – {job.clientName ?? ''}
                </Typography>
                <Typography sx={{ fontFamily: 'inherit', fontSize: '12pt' }}>
                    Job site address – {job.address}
                </Typography>
            </Box>

            {/* ─── Instructions ──────────────────────────────────────── */}
            <Table>
                <Row header cells={['SAFE WORK METHOD STATEMENT']} />
                <Row cells={[<b key="s">Steps for filling out</b>]} />
                <Row
                    cells={[
                        '1. Discuss with relevant employees, contractors and HSRs what work will be high-risk, the tasks, and associated hazards, risks and controls.',
                    ]}
                />
                <Row
                    cells={[
                        '2. In the ‘What are the tasks involved?’ column, list the work tasks in sequence to how they will be carried out.',
                    ]}
                />
                <Row
                    cells={[
                        '3. In the ‘What are the hazards and risks?’ column, list the hazards and risks for each work task.',
                    ]}
                />
                <Row
                    cells={[
                        <>
                            4. In the ‘How will the hazards and risks be controlled?’ column, select the hazard or risk
                            and then work through the control levels 1 – 4 from top to bottom. Choose a control measure
                            (and how it is to be used) that is as close to level 1 as is reasonably practicable.
                            <br />
                            <b>Control levels</b>
                            <br />
                            1. Eliminate any risk to health or safety associated with construction work.
                            <br />
                            2. Reduce the risk to health or safety by any one or any combination of the following:
                            <ul style={{ margin: '2px 0 2px 18px', padding: 0 }}>
                                <li>Substituting a new activity, procedure, plant, process or substance</li>
                                <li>
                                    Isolating persons from the hazard, such as barricading, fencing or guard railing, or
                                </li>
                                <li>Using engineering controls, such as mechanical or electrical devices.</li>
                            </ul>
                            3. Use administrative controls, such as changing the way the work is done.
                            <br />
                            4. Provide appropriate personal protective equipment.
                        </>,
                    ]}
                />
                <Row
                    cells={[
                        '5. Brief each team member on this SWMS before commencing work. Ensure team knows that work is to immediately stop if the SWMS is not being followed.',
                    ]}
                />
                <Row
                    cells={[
                        '6. Observe work being carried out. If controls are not adequate, stop the work, review the SWMS, adjust as required and re-brief the team.',
                    ]}
                />
                <Row cells={['7. Retain this SWMS for the duration of the high-risk construction work.']} />
                <Row
                    cells={[
                        <i key="n">
                            Note: The SWMS’s provided here will need to be reviewed and modified on site to control site
                            specific hazards and risks.
                        </i>,
                    ]}
                />
            </Table>

            {/* ─── Tasks ─────────────────────────────────────────────── */}
            <Table>
                <Row
                    header
                    cells={[
                        'What are the tasks involved?',
                        'What are the hazards and risks?',
                        <>
                            How will hazards and risks be controlled?
                            <br />
                            <span style={{ fontWeight: 400 }}>
                                (describe the control measures and how they will be used)
                            </span>
                        </>,
                    ]}
                />
                <Row
                    cells={[
                        <i key="t">
                            Think about the worksite and each stage of the project, including preparation and clean-up.
                        </i>,
                        '',
                        '',
                    ]}
                />
                {tasks.length === 0 ? (
                    <Row cells={['No SWMS tasks are selected for this job.', '', '']} />
                ) : (
                    tasks.map((t) => (
                        <Row
                            key={t.id}
                            cells={[
                                <b key="a">{t.task}</b>,
                                <span key="b" style={{ whiteSpace: 'pre-line' }}>
                                    {t.hazardsRisks}
                                </span>,
                                <span key="c" style={{ whiteSpace: 'pre-line' }}>
                                    {t.controlMeasures}
                                </span>,
                            ]}
                        />
                    ))
                )}
            </Table>

            {/* ─── Statement ─────────────────────────────────────────── */}
            <Table>
                <Row header cells={['Safe Work Method Statement', '']} />
                <Row
                    cells={[
                        <i key="n">
                            Note: A site-specific SWMS may be required before any high-risk construction work is
                            commenced.
                        </i>,
                        '',
                    ]}
                />
                <Row cells={['Employer:', EMPLOYER]} />
                <Row cells={['Subcontractor (if any):', EMPLOYER]} />
                <Row
                    cells={[
                        'Name & Position of Person responsible for ensuring compliance with this SWMS:',
                        signoff.personResponsible ?? '',
                    ]}
                />
                <Row cells={['Date:', signedDate]} />
                <Row cells={['High-risk job:', 'N/A']} />
                <Row cells={['Scope:', 'CARPENTRY']} />
                <Row cells={['Location:', job.address]} />
                <Row cells={['Persons on site involved in the works:', '']} />
                <Row cells={['Tools & Equipment required:', 'Saws, Drills, Basic hand tools, Cordless framing gun']} />
            </Table>

            {/* ─── Sign-off ──────────────────────────────────────────── */}
            <Table>
                <Row header cells={['Signed & declared as true by (PRINT NAME)', 'SIGNATURE', 'Dated']} />
                <Row
                    cells={[
                        <b key="n">{signoff.staffName}</b>,
                        signoff.signatureUrl === null ? (
                            <i key="s">No signature on record</i>
                        ) : (
                            <img
                                key="s"
                                src={signoff.signatureUrl}
                                alt={`Signature of ${signoff.staffName}`}
                                style={{ height: 90 }}
                            />
                        ),
                        signedDate,
                    ]}
                />
            </Table>

            {/* ─── Risk assessment example ───────────────────────────── */}
            <Table>
                <Row
                    header
                    cells={[
                        'Stage',
                        'Steps taken in activity',
                        'Identify Hazards/Risk',
                        'Risk Rating (H/M/L)',
                        'Control Measures to be taken',
                        'New Risk Rating (H/M/L)',
                    ]}
                />
                <Row
                    cells={[
                        '1',
                        'Cutting',
                        'insufficient lighting, excess noise, dust',
                        'L',
                        'adequate lighting, wearing earmuffs and using dust extraction bags on saws',
                        'L',
                    ]}
                />
                <Row
                    cells={[
                        '1',
                        'Nailing',
                        'insufficient lighting, excess noise, eye damage',
                        'L',
                        'adequate lighting, wearing earmuffs and wearing protective gear',
                        'L',
                    ]}
                />
                <Row
                    cells={[
                        '1',
                        'Working at heights',
                        'Risk of falling, dropping tools',
                        'M',
                        'mobile scaffold, correct ladders, safety barriers and correct protective gear',
                        'M',
                    ]}
                />
            </Table>

            <Table>
                <Row
                    header
                    cells={['Consequence', 'Likelihood/Probability', 'A – Very Likely', 'B – Likely', 'C – Unlikely']}
                />
                <Row cells={['Major', '1', 'H', 'H', 'M']} />
                <Row cells={['Moderate', '2', 'H', 'M', 'L']} />
                <Row cells={['Minor', '3', 'M', 'L', 'L']} />
            </Table>

            {/* ─── Hierarchy of controls ─────────────────────────────── */}
            <Table>
                <Row header cells={['Hazard/Risk Control Options (Hierarchy of Controls)']} />
                <Row cells={['1. ELIMINATION, can the risk or hazard be totally eliminated?']} />
                <Row
                    cells={[
                        '2. SUBSTITUTION, can the risk or hazard be replaced with a less hazardous method, material or system?',
                    ]}
                />
                <Row
                    cells={[
                        '3. ISOLATION, can the hazard or risk, be distanced from persons or can it be enclosed to prevent entry/access?',
                    ]}
                />
                <Row
                    cells={[
                        '4. ENGINEERING CONTROLS, can the hazard or risk be guarded or made safe by engineering methods?',
                    ]}
                />
                <Row
                    cells={[
                        '5. ADMINISTRATIVE CONTROLS, can, training, increased supervision, rotation or signage assist?',
                    ]}
                />
                <Row
                    cells={['6. PERSONAL PROTECTIVE EQUIPMENT, can PPE protect the worker from the hazard or risk?']}
                />
            </Table>

            <Table>
                <Row
                    header
                    cells={[
                        'Risk Ranking',
                        'Consequence',
                        'Quality',
                        'Safety',
                        'Environment',
                        'Financial Equivalent / Impact on Operations',
                    ]}
                />
                <Row
                    cells={[
                        'H',
                        'Major',
                        'Key customer contract cancellation; negative media exposure; loss of industry confidence; imposed liquidated damages. Product safety issues, contract cancellation, non-agreed contract variations, time extension, loss of reputation, loss of customer confidence, imposed liquidated damages',
                        'Fatality. Serious/Moderate injury/disease. Permanent disability (LTI)',
                        'Possibility to cause long term environmental issue (harm), clean up costs >$10,000, non compliance/breach of planning conditions with potential for legal, stop work notice issued due stakeholder concerns.',
                        'More than $500K damage – Major long term impact on operations. $100K to $500K damage – Moderate long term/major short term impact on operations',
                    ]}
                />
                <Row
                    cells={[
                        'M',
                        'Moderate',
                        'Significant contract variation, Potential for liquidated damages, < $200k; project defect.',
                        'Moderate/Minor injury or illness (MTI)',
                        'Possible short term issue, potential for stakeholder disruption/complaints',
                        '$50K to $100K damage – Minor long term/moderate short term impact on operations',
                    ]}
                />
                <Row
                    cells={[
                        'L',
                        'Minor',
                        'Customer complaints in products and services, agreed extensions of time and minor variations',
                        'Minor injury or illness (FAI)',
                        'Minor possibility of environmental issue (short term < 1 day)',
                        'Up to $50K damage – Minor short term impact on operations',
                    ]}
                />
            </Table>

            <Table>
                <Row
                    header
                    cells={[
                        'Consequence',
                        'Description of Consequence or Impact',
                        'Likelihood / Probability',
                        '',
                        'Level of Harm or Risk Ranking',
                        '',
                    ]}
                />
                <Row
                    cells={[
                        '1 Major',
                        'Potential death, permanent disability or major structural failure/damage. Off-site environmental discharge/release not contained and significant long-term environmental harm.',
                        'A Very Likely',
                        'Could happen frequently',
                        'H',
                        'High level of risk',
                    ]}
                />
                <Row
                    cells={[
                        '2 Moderate',
                        'Potential temporary disability or minor structural failure/damage. On-site environmental discharge/release contained, minor remediation required, short-term environmental harm.',
                        'B Likely',
                        'Could happen occasionally',
                        'M',
                        'Medium level of risk',
                    ]}
                />
                <Row
                    cells={[
                        '3 Minor',
                        'Incident that has the potential to cause persons to require first aid. On-site environmental discharge/release immediately contained minor level clean up with no short-term environmental harm.',
                        'C Unlikely',
                        'May occur only in exceptional circumstances',
                        'L',
                        'Low level of risk',
                    ]}
                />
            </Table>
        </Box>
    );
}

const PRINT_CLASS = 'swms-print-dialog';

export function SwmsRecordDialog({
    jobId,
    recordId,
    onClose,
}: {
    jobId: number;
    /** The sign-off to show; null when closed. */
    recordId: number | null;
    onClose: () => void;
}): React.JSX.Element {
    const [data, setData] = useState<SwmsRecordData | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setData(null);
        setError(null);
        if (recordId === null) {
            return;
        }
        let cancelled = false;
        apiFetch<SwmsRecordData>(`/jobs/${String(jobId)}/swms-records/${String(recordId)}`)
            .then((d) => {
                if (!cancelled) {
                    setData(d);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Could not load the SWMS record');
                }
            });
        return () => {
            cancelled = true;
        };
    }, [jobId, recordId]);

    // Opens once loaded (or failed), so there is no empty dialog in between.
    const open = recordId !== null && (data?.signoff.id === recordId || error !== null);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className={PRINT_CLASS}>
            {open ? (
                <GlobalStyles
                    styles={{
                        '@media print': {
                            '@page': { size: 'A4', margin: '12mm' },
                            'html, body': { background: '#fff !important' },
                            [`body > *:not(.${PRINT_CLASS})`]: { display: 'none !important' },
                            [`.${PRINT_CLASS}, .${PRINT_CLASS} .MuiDialog-container, .${PRINT_CLASS} .MuiDialog-paper, .${PRINT_CLASS} .MuiDialogContent-root`]:
                                {
                                    position: 'static !important',
                                    display: 'block !important',
                                    overflow: 'visible !important',
                                    maxHeight: 'none !important',
                                    maxWidth: 'none !important',
                                    width: 'auto !important',
                                    margin: '0 !important',
                                    padding: '0 !important',
                                    boxShadow: 'none !important',
                                    background: '#fff !important',
                                },
                            [`.${PRINT_CLASS} .MuiBackdrop-root, .${PRINT_CLASS} .no-print`]: {
                                display: 'none !important',
                            },
                        },
                    }}
                />
            ) : null}
            <DialogTitle className="no-print" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                SWMS
                {data !== null ? (
                    <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                        {data.signoff.staffName} · Job {data.job.jobNumber}
                    </Typography>
                ) : null}
                <Box sx={{ flex: 1 }} />
                <IconButton onClick={onClose} size="small" aria-label="Close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {error !== null ? <Alert severity="error">{error}</Alert> : null}
                {data !== null ? <SwmsDocument data={data} /> : null}
            </DialogContent>
            <DialogActions className="no-print" sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose}>Close</Button>
                <Button
                    variant="contained"
                    startIcon={<PrintIcon />}
                    disabled={data === null}
                    onClick={() => {
                        window.print();
                    }}
                >
                    Print / Save as PDF
                </Button>
            </DialogActions>
        </Dialog>
    );
}
